type StepState = 'done' | 'running' | 'waiting';

/**
 * Simple stable hash → picks the same variant for the same toolName every time.
 */
function stableIndex(str: string, len: number): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % len;
}

function pick(variants: string[], toolName: string): string {
  return variants[stableIndex(toolName, variants.length)]!;
}

// ---------------------------------------------------------------------------
// Curated narratives for known tool paths / services
// ---------------------------------------------------------------------------

const TOOL_NARRATIVES: Record<string, Record<StepState, string[]>> = {
  'coco/conversation-memory': {
    done: ['Recalled earlier results', 'Checked conversation memory'],
    running: ['Recalling earlier results', 'Checking conversation memory'],
    waiting: ['Recall earlier results'],
  },
  'coco/sleep': {
    done: ['Resumed after waiting', 'Finished sleeping'],
    running: ['Sleeping', 'Pausing the run'],
    waiting: ['Sleep'],
  },
  'search-api/google-search': {
    done: ['Searched the web', 'Scoured the web', 'Combed through the web'],
    running: [
      'Searching the web',
      'Scouring the web',
      'Combing through the web',
    ],
    waiting: ['Search the web'],
  },
  'google-keyword/top-keys': {
    done: ['Looked up top keywords', 'Surfaced top keywords'],
    running: ['Looking up top keywords', 'Surfacing top keywords'],
    waiting: ['Look up top keywords'],
  },
  twitter: {
    done: ['Checked Twitter', 'Browsed Twitter', 'Scanned Twitter'],
    running: ['Checking Twitter', 'Browsing Twitter', 'Scanning Twitter'],
    waiting: ['Check Twitter'],
  },
  linkedin: {
    done: ['Checked LinkedIn', 'Browsed LinkedIn', 'Peeked at LinkedIn'],
    running: ['Checking LinkedIn', 'Browsing LinkedIn', 'Peeking at LinkedIn'],
    waiting: ['Check LinkedIn'],
  },
  airtable: {
    done: ['Checked Airtable', 'Consulted Airtable', 'Pulled from Airtable'],
    running: [
      'Checking Airtable',
      'Consulting Airtable',
      'Pulling from Airtable',
    ],
    waiting: ['Check Airtable'],
  },
  slack: {
    done: ['Checked Slack', 'Skimmed Slack'],
    running: ['Checking Slack', 'Skimming Slack'],
    waiting: ['Check Slack'],
  },
  hubspot: {
    done: ['Checked HubSpot', 'Consulted HubSpot'],
    running: ['Checking HubSpot', 'Consulting HubSpot'],
    waiting: ['Check HubSpot'],
  },
  salesforce: {
    done: ['Checked Salesforce', 'Consulted Salesforce'],
    running: ['Checking Salesforce', 'Consulting Salesforce'],
    waiting: ['Check Salesforce'],
  },
  notion: {
    done: ['Checked Notion', 'Browsed Notion'],
    running: ['Checking Notion', 'Browsing Notion'],
    waiting: ['Check Notion'],
  },
  jira: {
    done: ['Checked Jira', 'Consulted Jira'],
    running: ['Checking Jira', 'Consulting Jira'],
    waiting: ['Check Jira'],
  },
  github: {
    done: ['Checked GitHub', 'Browsed GitHub'],
    running: ['Checking GitHub', 'Browsing GitHub'],
    waiting: ['Check GitHub'],
  },
  stripe: {
    done: ['Checked Stripe', 'Consulted Stripe'],
    running: ['Checking Stripe', 'Consulting Stripe'],
    waiting: ['Check Stripe'],
  },
  zendesk: {
    done: ['Checked Zendesk', 'Consulted Zendesk'],
    running: ['Checking Zendesk', 'Consulting Zendesk'],
    waiting: ['Check Zendesk'],
  },
  intercom: {
    done: ['Checked Intercom', 'Consulted Intercom'],
    running: ['Checking Intercom', 'Consulting Intercom'],
    waiting: ['Check Intercom'],
  },
  shopify: {
    done: ['Checked Shopify', 'Browsed Shopify'],
    running: ['Checking Shopify', 'Browsing Shopify'],
    waiting: ['Check Shopify'],
  },
  mailchimp: {
    done: ['Checked Mailchimp', 'Consulted Mailchimp'],
    running: ['Checking Mailchimp', 'Consulting Mailchimp'],
    waiting: ['Check Mailchimp'],
  },
  'google-workspace': {
    done: ['Used Google Workspace', 'Tinkered with Google Workspace'],
    running: ['Using Google Workspace', 'Tinkering with Google Workspace'],
    waiting: ['Use Google Workspace'],
  },
  'google-sheets': {
    done: ['Updated Google Sheets', 'Tinkered with Google Sheets'],
    running: ['Updating Google Sheets', 'Tinkering with Google Sheets'],
    waiting: ['Update Google Sheets'],
  },
};

// ---------------------------------------------------------------------------
// Action verb variants for pattern-matched tool names
// ---------------------------------------------------------------------------

type VerbVariants = Record<StepState, string[]>;

const ACTION_VERBS: Record<string, VerbVariants> = {
  search: {
    done: ['Searched', 'Scoured', 'Combed through'],
    running: ['Searching', 'Scouring', 'Combing through'],
    waiting: ['Search'],
  },
  find: {
    done: ['Found data in', 'Dug through'],
    running: ['Finding data in', 'Digging through'],
    waiting: ['Find data in'],
  },
  get: {
    done: ['Pulled from', 'Fetched from', 'Grabbed data from'],
    running: ['Pulling from', 'Fetching from', 'Grabbing data from'],
    waiting: ['Pull from'],
  },
  fetch: {
    done: ['Pulled from', 'Fetched from'],
    running: ['Pulling from', 'Fetching from'],
    waiting: ['Pull from'],
  },
  list: {
    done: ['Listed', 'Gathered items from', 'Rounded up'],
    running: ['Listing', 'Gathering items from', 'Rounding up'],
    waiting: ['List'],
  },
  create: {
    done: ['Created in', 'Wrote to', 'Put together in'],
    running: ['Creating in', 'Writing to', 'Putting together in'],
    waiting: ['Create in'],
  },
  write: {
    done: ['Wrote to', 'Drafted in'],
    running: ['Writing to', 'Drafting in'],
    waiting: ['Write to'],
  },
  update: {
    done: ['Updated', 'Tweaked', 'Modified'],
    running: ['Updating', 'Tweaking', 'Modifying'],
    waiting: ['Update'],
  },
  delete: {
    done: ['Removed from', 'Cleaned up'],
    running: ['Removing from', 'Cleaning up'],
    waiting: ['Remove from'],
  },
  send: {
    done: ['Sent via', 'Dispatched via'],
    running: ['Sending via', 'Dispatching via'],
    waiting: ['Send via'],
  },
  analyze: {
    done: ['Analyzed', 'Studied', 'Examined'],
    running: ['Analyzing', 'Studying', 'Examining'],
    waiting: ['Analyze'],
  },
  sync: {
    done: ['Synced', 'Synchronized'],
    running: ['Syncing', 'Synchronizing'],
    waiting: ['Sync'],
  },
  query: {
    done: ['Queried', 'Looked up'],
    running: ['Querying', 'Looking up'],
    waiting: ['Query'],
  },
  export: {
    done: ['Exported from'],
    running: ['Exporting from'],
    waiting: ['Export from'],
  },
  import: {
    done: ['Imported to'],
    running: ['Importing to'],
    waiting: ['Import to'],
  },
};

// ---------------------------------------------------------------------------
// Fallback verb variants when no action is detected
// ---------------------------------------------------------------------------

const FALLBACK_VERBS: VerbVariants = {
  done: ['Checked', 'Consulted', 'Looked into'],
  running: ['Checking', 'Consulting', 'Looking into'],
  waiting: ['Check'],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function prettifyService(raw: string): string {
  const KNOWN: Record<string, string> = {
    'google-workspace': 'Google Workspace',
    'google-keyword': 'Google Keywords',
    'google-sheets': 'Google Sheets',
    'search-api': 'the web',
    coco: 'Coco',
    hubspot: 'HubSpot',
    github: 'GitHub',
    bigquery: 'BigQuery',
    snowflake: 'Snowflake',
  };
  return (
    KNOWN[raw] ??
    raw
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

function extractAction(segment: string): string | null {
  const words = segment
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .split('-');

  for (const word of words) {
    if (word in ACTION_VERBS) {
      return word;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function translateToolName(toolName: string, state: StepState): string {
  // 1. Exact match
  const exact = TOOL_NARRATIVES[toolName];
  if (exact !== undefined) {
    const text = pick(exact[state], toolName);
    return state === 'running' ? `${text}…` : text;
  }

  const segments = toolName.split('/').filter((s) => s.length > 0);
  if (segments.length === 0) {
    return state === 'running' ? 'Working…' : 'Working';
  }

  const serviceKey = segments[0]!;
  const service = prettifyService(serviceKey);
  const lastSegment = segments[segments.length - 1]!;
  const action = extractAction(lastSegment);

  // 2. Action verb + service name
  if (action !== null) {
    const verb = pick(ACTION_VERBS[action]![state], toolName);
    const ellipsis = state === 'running' ? '…' : '';
    return `${verb} ${service}${ellipsis}`;
  }

  // 3. Service-level match
  const serviceMatch = TOOL_NARRATIVES[serviceKey];
  if (serviceMatch !== undefined) {
    const text = pick(serviceMatch[state], toolName);
    return state === 'running' ? `${text}…` : text;
  }

  // 4. Fallback with variants
  const fallbackVerb = pick(FALLBACK_VERBS[state], toolName);
  const ellipsis = state === 'running' ? '…' : '';
  return `${fallbackVerb} ${service}${ellipsis}`;
}
